/**
 * Predictive Models — hub
 *
 * Cards-per-family overview. Each card represents one family of
 * predictive workloads. Propensity Scoring and Contact Optimization
 * Scoring are shipping today; Forecasting / Anomaly Detection /
 * Recommendations slot in here as we add them.
 *
 * Visibility model
 * ----------------
 * - The route is registered unconditionally.
 * - Each family card consults its own feature flag via `useFeature`.
 *   Enabled flags get a "Manage" CTA → family list page.
 *   Disabled flags render as a "Available — enable in Settings" hint
 *   so admins can discover the surface even before they turn it on.
 * - When ZERO predictive families are enabled and the user lands on
 *   /predictive-models, we still render the hub so they understand
 *   what's available — the sidebar entry only shows up when at least
 *   one is enabled, but direct links shouldn't error out.
 *
 * Why a hub layer with one family
 * --------------------------------
 * It looks like overhead today, but the hub is the affordance that
 * communicates "there will be more model families here". When
 * forecasting ships, this page already explains the IA — no rename or
 * route shuffle needed. The list page (/predictive-models/propensity)
 * remains the deeper-link people bookmark; the hub just makes the
 * structure scannable.
 */
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ModelTrainingRounded from '@mui/icons-material/ModelTrainingRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import RadarRounded from '@mui/icons-material/RadarRounded';
import RecommendRounded from '@mui/icons-material/RecommendRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';
import { useFeature } from '../../ui/FeaturesProvider';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';

interface FamilyCard {
  key: string;
  name: string;
  description: string;
  icon: typeof ModelTrainingRounded;
  /** Feature flag key (FEATURE_REGISTRY) that gates this family. */
  featureKey: string;
  /** Path to the family list page. */
  href: string;
  /** Set to true for families that are roadmapped but not shipping yet. */
  comingSoon?: boolean;
}

// Hand-curated registry of model families. New families show up here as
// we ship them (each with their own feature flag in the backend
// FEATURE_REGISTRY). Order is the order the cards render in.
const FAMILIES: FamilyCard[] = [
  {
    key: 'propensity',
    name: 'Propensity Scoring',
    description:
      'Predict binary outcomes — lapse, churn, default, conversion — and surface the drivers behind each prediction. Trained per-tenant against a managed Redshift mart with SHAP-based explanations.',
    icon: ModelTrainingRounded,
    featureKey: 'propensity_scoring',
    href: '/predictive-models/propensity',
  },
  {
    key: 'contact-optimization',
    name: 'Contact Optimization Scoring',
    description:
      'Predicts each lead\'s pickup chance by hour and blends it with deal likelihood. Powers Contact Pulse\'s best-hour recommendations, Queue\'s call ranking, and Plan\'s staffing.',
    icon: PhoneInTalkRounded,
    featureKey: 'contact_optimization',
    href: '/predictive-models/contact-optimization',
  },
  {
    key: 'forecasting',
    name: 'Forecasting',
    description:
      'Time-series forecasts for revenue, headcount, MRR, and other business metrics, with drill-down into driver attribution.',
    icon: TrendingUpRounded,
    // No feature flag yet; treated as disabled.
    featureKey: '__forecasting_not_implemented__',
    href: '/predictive-models/forecasting',
    comingSoon: true,
  },
  {
    key: 'anomaly',
    name: 'Anomaly Detection',
    description:
      'Surface unusual patterns in operational metrics — unexpected drops, spikes, or distribution shifts — with confidence intervals and root-cause hints.',
    icon: RadarRounded,
    featureKey: '__anomaly_not_implemented__',
    href: '/predictive-models/anomaly',
    comingSoon: true,
  },
  {
    key: 'recommendations',
    name: 'Recommendations',
    description:
      'Personalised next-best-action suggestions for retention and growth campaigns, scored against the same propensity infrastructure.',
    icon: RecommendRounded,
    featureKey: '__recommendations_not_implemented__',
    href: '/predictive-models/recommendations',
    comingSoon: true,
  },
];

export default function PredictiveModelsHubPage() {
  return (
    <Box>
      <SettingsPageHeader
        description="Configure and manage every predictive model family Klaris supports for your workspace. Enabling a family in Settings → Features adds the corresponding configuration surface here."
      />
      <Stack spacing={1.5}>
        {FAMILIES.map((f) => (
          <FamilyCardItem key={f.key} family={f} />
        ))}
      </Stack>
    </Box>
  );
}

function FamilyCardItem({ family }: { family: FamilyCard }) {
  const navigate = useNavigate();
  const feature = useFeature(family.featureKey);
  // `feature` is null for unknown keys (the placeholder roadmap entries
  // we haven't built yet). Treat that as disabled.
  const enabled = !family.comingSoon && Boolean(feature?.enabled);
  const Icon = family.icon;

  // Disabled / coming-soon variant — informational, no click-through.
  if (!enabled) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={(t) => ({
                width: 40,
                height: 40,
                borderRadius: `${t.ds.radius.sm}px`,
                bgcolor: t.tokens.surface.sunken,
                color: t.tokens.text.tertiary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              })}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {family.name}
                </Typography>
                {family.comingSoon ? (
                  <Chip size="small" label="Coming soon" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
                ) : (
                  <Chip size="small" label="Available" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {family.description}
              </Typography>
              {!family.comingSoon && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Enable under <strong>Settings → Features</strong> to configure.
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Enabled — full card click navigates to the family list page.
  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => navigate(family.href)}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={(t) => ({
                width: 40,
                height: 40,
                borderRadius: `${t.ds.radius.sm}px`,
                bgcolor: t.tokens.brand.primarySoft,
                color: t.tokens.brand.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              })}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {family.name}
                </Typography>
                <Chip size="small" label="Enabled" color="success" sx={{ height: 20, fontSize: '0.6875rem' }} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {family.description}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Button
                variant="text"
                endIcon={<ArrowForwardRounded />}
                onClick={(e) => { e.stopPropagation(); navigate(family.href); }}
              >
                Manage
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
